/**
 * ApiHealthBanner — RESIL-M1
 *
 * Banner no intrusivo del DS PTEL. Solo visible si una API crítica está caída.
 * Sin hover-glow verde. Cambios sutiles de opacidad/borde según DS.
 *
 * Endpoints críticos: DERA_g12, CartoCiudad.
 * Endpoints informativos (sin banner): CDAU, Nominatim, Overpass.
 *
 * @version 1.0.0
 */
import React, { type FC, type CSSProperties, useEffect, useRef } from 'react'
import { colors, typography, spacing } from '@/design-system/v2'
import { useApiHealth } from '../../hooks/useApiHealth'

// Clave de sessionStorage para contar apariciones por endpoint en la sesión
const SEEN_KEY = 'ptel_api_banner_seen'

function getSeenCounts(): Record<string, number> {
  try { return JSON.parse(sessionStorage.getItem(SEEN_KEY) ?? '{}') } catch { return {} }
}
function incrementSeen(id: string) {
  const counts = getSeenCounts()
  counts[id] = (counts[id] ?? 0) + 1
  try { sessionStorage.setItem(SEEN_KEY, JSON.stringify(counts)) } catch { /* noop */ }
}
/** Suprime el banner de un endpoint concreto si ya se ha mostrado ≥2 veces en la sesión */
function isSuppressed(id: string): boolean {
  return (getSeenCounts()[id] ?? 0) >= 2
}

// ─── SONDEO-APP: hora del sondeo y cruce por URL ─────────────────────────────

/**
 * Clave de cruce entre las dos listas del Worker: la URL sin consulta ni ancla.
 * `/health/apis` trae `…/wfs?SERVICE=WFS&REQUEST=GetCapabilities` y
 * `/health/endpoint-drift` trae `…/wfs` para el mismo servicio, con nombres distintos.
 * Los `CDAU_R2_*` no traen `url` → `null` (no cruzan con nada).
 */
export function claveUrl(url?: string | null): string | null {
  if (!url) return null
  const limpia = url.split('#')[0].split('?')[0].replace(/\/+$/, '').toLowerCase()
  return limpia || null
}

/**
 * Hora peninsular (HH:MM) del sondeo a partir del `ts` UTC del Worker.
 * Sin `ts` o con `ts` ilegible → `null`: el aviso sale sin hora, no se rompe (DOP-9 a).
 */
export function horaDelSondeo(ts?: string | null): string | null {
  if (!ts) return null
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return null
  try {
    return new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Madrid',
    }).format(d)
  } catch {
    return null
  }
}

/** «en el sondeo de las 08:00» · sin hora: «en el último sondeo» */
function textoSondeo(ts?: string | null): string {
  const hora = horaDelSondeo(ts)
  return hora ? `en el sondeo de las ${hora}` : 'en el último sondeo'
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ApiHealthBanner: FC = () => {
  const { loading, hasCriticalIssues, endpoints, driftDetected, driftChanges, healthTs, driftTs } = useApiHealth()

  // Calcular listas ANTES de cualquier return condicional (reglas de Hooks)
  const caidas = endpoints.filter(ep => ep.critical && !ep.ok)
  const caidasVisibles = caidas.filter(ep => !isSuppressed(ep.name))
  // BUG-UI-04: REDIAM_HIDRAULICAS se sirve desde R2 en producción; el WFS vivo solo
  // es fallback. Mostrar su DEAD a municipios sin infraestructura hidráulica genera
  // falsos positivos. Se excluye del banner de drift hasta que exista contexto de tipología.
  const DRIFT_BANNER_EXCLUDED = new Set(['REDIAM_HIDRAULICAS', 'REDIAM_hidraulicas'])
  // SONDEO-APP (DOP-4 a · DOP-8 a): un aviso por servicio. Si la misma URL está caída
  // como crítica en `/health/apis`, manda ese aviso y su DEAD de drift no sale.
  // Se cruza contra `caidas` (no contra las visibles): si el aviso crítico ya está
  // suprimido tras 2 apariciones, su gemelo de drift tampoco reaparece.
  const urlsCriticasCaidas = new Set(
    caidas.map(ep => claveUrl(ep.url)).filter((u): u is string => u !== null)
  )
  const driftBannerVisibles = driftChanges
    .filter(e => ['MOVED', 'DEAD', 'RESURRECTED'].includes(e.type))
    .filter(e => !DRIFT_BANNER_EXCLUDED.has(e.endpointId))
    .filter(e => {
      if (e.type !== 'DEAD') return true
      const clave = claveUrl(e.url)
      return clave === null || !urlsCriticasCaidas.has(clave)
    })
    .filter(e => !isSuppressed(e.endpointId))

  // Ref para evitar re-registrar en cada render sin violación de Hooks
  const registeredRef = useRef<string>('')
  const visiblesKey = [
    ...caidasVisibles.map(e => e.name),
    ...driftBannerVisibles.map(e => e.endpointId),
  ].join(',')

  // useEffect siempre antes de cualquier return condicional
  useEffect(() => {
    if (!visiblesKey || visiblesKey === registeredRef.current) return
    registeredRef.current = visiblesKey
    caidasVisibles.forEach(ep => incrementSeen(ep.name))
    driftBannerVisibles.forEach(e => incrementSeen(e.endpointId))
  // visiblesKey es estable por render; caidasVisibles/driftBannerVisibles son derivados
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblesKey])

  // Returns condicionales DESPUÉS de todos los Hooks
  if (loading || (!hasCriticalIssues && !driftDetected)) return null
  if (caidasVisibles.length === 0 && driftBannerVisibles.length === 0) return null

  const containerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing[3],
    padding: `${spacing[3]} ${spacing[4]}`,
    marginBottom: spacing[4],
    backgroundColor: colors.bg.secondary,
    border: `1px solid ${colors.scores.low}`,   // ámbar #FBBF24
    borderLeft: `3px solid ${colors.scores.low}`,
    borderRadius: '6px',
    fontFamily: typography.fontFamily.sans,
  }

  const iconStyle: CSSProperties = {
    flexShrink: 0,
    width: '16px',
    height: '16px',
    marginTop: '1px',
    color: colors.scores.low,
  }

  const textStyle: CSSProperties = {
    fontSize: typography.fontSize.sm,
    color: colors.fg.secondary,
    lineHeight: '1.5',
    margin: 0,
  }

  const accentStyle: CSSProperties = {
    color: colors.scores.low,
    fontWeight: typography.fontWeight.semibold,
  }

  const nombresLegibles: Record<string, string> = {
    DERA_g12:           'DERA (IDEA Andalucía)',
    CartoCiudad:        'CartoCiudad (IGN)',
    CDAU_R2:            'CDAU offline (R2)',
    // RESIL-WFS-S7
    CDAV_hidrografia:   'CDAV Hidrografía (IECA)',
    CDAV_tejido:        'CDAV Tejido Económico (IECA)',
    REDIAM_hidraulicas: 'REDIAM Hidráulicas (Junta)',
  }

  // Solo MOVED/DEAD/RESURRECTED activan banner visible — ya filtrado en driftBannerVisibles
  const driftBannerEvents = driftBannerVisibles

  return (
    <>
      {/* Banner de APIs caídas (RESIL-M1) */}
      {caidasVisibles.length > 0 && (
        <div
          data-testid="api-health-banner"
          role="alert"
          aria-live="polite"
          style={containerStyle}
        >
          <svg style={iconStyle} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
          </svg>
          <p style={textStyle}>
            {caidasVisibles.map((ep, i) => (
              <React.Fragment key={ep.name}>
                <span style={accentStyle}>{nombresLegibles[ep.name] ?? ep.name}</span>
                {i < caidasVisibles.length - 1 ? ' y ' : ''}
              </React.Fragment>
            ))}
            {' '}
            {caidasVisibles.length === 1 ? 'no respondía' : 'no respondían'}
            {` ${textoSondeo(healthTs)}\u00A0— los resultados de geocodificación pueden ser incompletos.`}
          </p>
        </div>
      )}

      {/* Banners de drift de endpoints (RESIL-WFS-S0) */}
      {driftBannerEvents.map(ev => {
        const driftColors: Record<string, string> = {
          MOVED: '#FBBF24', DEAD: '#EF4444', RESURRECTED: '#34D399',
        }
        const driftMsg: Record<string, string> = {
          MOVED: 'ha sido migrado silenciosamente',
          DEAD:  `no respondía ${textoSondeo(driftTs ?? ev.detectedAt)} · puede estar en mantenimiento. Los resultados se obtendrán de fuentes alternativas`,
          RESURRECTED: 'reapareció con nueva ubicación — verificar actualización',
        }
        const c = driftColors[ev.type] ?? '#FBBF24'
        return (
          <div
            key={ev.endpointId}
            data-testid={`drift-banner-${ev.type.toLowerCase()}`}
            role="alert"
            aria-live="polite"
            style={{ ...containerStyle, borderColor: c, borderLeftColor: c }}
          >
            <svg style={{ ...iconStyle, color: c }} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
            </svg>
            <p style={textStyle}>
              <span style={{ ...accentStyle, color: c }}>{ev.endpointId}</span>
              {' '}{driftMsg[ev.type] ?? 'ha cambiado'}
              {ev.toUrl ? <> — nueva URL: <code style={{ fontSize: '11px' }}>{ev.toUrl}</code></> : null}
              {'.'}
            </p>
          </div>
        )
      })}
    </>
  )
}

export default ApiHealthBanner
